<template>
  <div class="modal-overlay w-full h-full">
    <div class="popup popup--label">
      <popup-header :title="$t('app.list.label')" @close="closePopup" />
      <div
        class="popup__content popup__content--label"
        :class="{ 'overflow-y-auto': labels.length > 7 }"
      >
        <div
          v-if="!showCreate"
          class="create-new-label"
          @click="showCreate = true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M7.08338 1.83429C7.08338 1.28278 6.63504 0.835693 6.08198 0.835693C5.52892 0.835693 5.08058 1.28278 5.08058 1.83429V4.95648H1.85931C1.30625 4.95648 0.85791 5.40357 0.85791 5.95508C0.85791 6.5066 1.30625 6.95368 1.85931 6.95368H5.08058V10.256C5.08058 10.8076 5.52892 11.2546 6.08198 11.2546C6.63504 11.2546 7.08338 10.8076 7.08338 10.256V6.95368H10.3047C10.8577 6.95368 11.3061 6.5066 11.3061 5.95508C11.3061 5.40357 10.8577 4.95648 10.3047 4.95648H7.08338V1.83429Z"
              fill="#2B5DE5"
            />
          </svg>
          <span
            class="text-primary-base text-xs font-medium dark:text-primary-100"
          >
            {{ $t("button.label.create") }}
          </span>
        </div>
        <Input
          v-else
          class="!h-10"
          v-model="newLabel"
          @enter="createLabel"
          :placeholder="$t('input.label.placeholder')"
          :maxlength="25"
          :darkModeClass="true"
        >
          <template v-slot:trailing>
            <Button class="primary" @click="createLabel">
              {{ $t("button.label.add") }}
            </Button>
          </template>
        </Input>
        <div
          v-for="label in labels"
          :key="label.id"
          class="popup__content__item--label"
        >
          <div class="flex flex-row gap-2 items-center">
            <div class="label-ball" :style="labelColor(label.color)"></div>
            <div
              :class="{ selected_item: label.id === selected }"
              class="indicator"
              @click="labelSelection(label.id)"
            ></div>
            <span class="dark:text-secondary-300">{{ label.title }}</span>
          </div>
          <img
            v-if="theme"
            src="@/assets/images/ellipsis-dark.svg"
            alt="others"
            class="h-6 w-6 cursor-pointer"
            @click="changeShowAction(label)"
          />
          <img
            v-else
            src="@/assets/images/ellipsis.svg"
            alt="others"
            class="h-6 w-6 cursor-pointer"
            @click="changeShowAction(label)"
          />
          <div
            v-if="showActions && focusedLabel.id === label.id"
            class="popup__other--label scale-125"
            v-click-outside="closeChangeLabel"
          >
            <div
              class="h-[18px] rounded-sm flex flex-row items-center px-1 overflow-hidden cursor-text"
              :style="labelColor(focusedLabel.color, true)"
              @click="changeToInput(focusedLabel.title)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 8 8"
                fill="none"
                class="flex-none h-2 w-2 stroke-current"
              >
                <path
                  d="M4.42003 1.19991L1.68337 4.09658C1.58003 4.20658 1.48003 4.42324 1.46003 4.57324L1.3367 5.65324C1.29337 6.04324 1.57337 6.30991 1.96003 6.24324L3.03337 6.05991C3.18337 6.03324 3.39337 5.92324 3.4967 5.80991L6.23337 2.91324C6.7067 2.41324 6.92003 1.84324 6.18337 1.14658C5.45003 0.456576 4.89337 0.699909 4.42003 1.19991Z"
                  stroke-width="0.5"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M3.96338 1.68335C4.10671 2.60335 4.85338 3.30668 5.78005 3.40002"
                  stroke-width="0.5"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M1 7.33325H7"
                  stroke-width="0.5"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span
                v-if="!showChangeLabel"
                class="flex-1 text-center text-[8px] font-500 overflow-hidden whitespace-nowrap overflow-ellipsis"
                >{{ focusedLabel.title }}
              </span>
              <input
                v-else
                class="h-min text-[8px] font-500 border-none !outline-none bg-transparent w-full"
                type="text"
                v-model="focusedLabel.title"
                ref="input"
                maxlength="25"
              />
            </div>
            <div class="flex flex-row justify-between items-center">
              <span
                class="text-[8px] font-medium text-black dark:text-secondary-200"
                >
                {{ $t("app.list.colors") }}
              </span
              >
              <img src="@/assets/images/palette.svg" class="h-[9px] w-[9px]" />
            </div>
            <div
              class="flex flex-row gap-0.5 pb-2 border-b border-greyscale-100 dark:border-[#333741]"
            >
              <div
                v-for="color in colors"
                :key="color"
                :style="labelColor(color)"
                class="h-[9px] w-[9px] cursor-pointer"
                @click="changeColor(color)"
              ></div>
            </div>
            <div class="flex flex-row justify-between items-center relative">
              <!-- <v-popover trigger="click">
                <div
                  class="flex justify-center items-center cursor-pointer p-[3px] h-[18px] w-[18px] rounded-full bg-greyscale-200 relative"
                >
                  <img src="@/assets/images/delete.svg" />
                </div>
                <template slot="popover">
                  <div class="flex flex-col gap-2">
                    <span>are you sure to delete this file?</span>
                    <div class="flex flex-row gap-2 justify-end">
                      <Button class="secondary h-[30px]">Cancel</Button
                      ><Button
                        class="primary h-[30px]"
                        @click="deleteLabel(label.id)"
                        >Delete</Button
                      >
                    </div>
                  </div>
                </template>
              </v-popover> -->
              <div
                class="flex justify-center items-center cursor-pointer p-[3px] h-[18px] w-[18px] rounded-full bg-greyscale-200 dark:bg-[#9519264d]"
                @click="deleteLabel(label)"
              >
                <img v-if="theme" src="@/assets/images/trash-dark.svg" />
                <img v-else src="@/assets/images/delete.svg" />
              </div>
              <Button
                class="primary h-4 text-[6px] px-2"
                @click="updateLabel"
                :disabled="!isLabelChanged"
                >{{ $t("button.general.update") }}</Button
              >
            </div>
          </div>
        </div>
      </div>
      <div class="popup__footer">
        <Button class="secondary" @click="closePopup()">
          {{ $t("button.general.cancel") }}
        </Button>
        <Button
          class="primary"
          @click="action()"
          :disabled="!isSelectedChanged"
        >
          {{ $t("button.general.confirm") }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import PopupHeader from "@/components/PopupHeader";
import Button from "@/components/Button";
import Input from "@/components/Input";
import { LABEL_COLORS } from "@/constants/index.js";

export default {
  name: "LabelPopup",
  components: { PopupHeader, Button, Input },
  props: {
    selectedLabelId: {
      type: Object,
    },
  },
  data() {
    return {
      labels: [],
      selected: null,
      focusedLabel: {},
      showCreate: false,
      showActions: false,
      showChangeLabel: false,
      newLabel: "",
      colors: LABEL_COLORS,
      DEFAULT_COLOR: "#0C4FFB",
    };
  },
  created() {
    this.theme = this.getTheme === "dark";
  },
  methods: {
    ...mapActions(["emitNotification", "emitConfirmData"]),
    closePopup() {
      this.showCreate = false;
      this.showActions = false;
      this.showChangeLabel = false;
      this.newLabel = "";
      this.$emit("close-popup");
    },
    action() {
      this.$emit("change-app-label", this.selected);
      this.$emit("close-popup");
      this.selected = null;
    },
    labelSelection(id) {
      this.selected = this.selected === id ? null : id;
    },
    labelColor(color, withText = false) {
      return withText
        ? `background-color: ${color}2E;color: ${color}`
        : `background-color: ${color};`;
    },
    createLabel() {
      if (this.newLabel.length === 0) {
        this.emitNotification({
          type: "error",
          text: this.$t("notification.label.cannot_empty"),
        });
      } else if (this.newLabel.length <= 25) {
        this.$emit("create-label", {
          title: this.newLabel,
          color: this.DEFAULT_COLOR,
        });
        this.showCreate = false;
        this.newLabel = "";
      }
    },
    deleteLabel(label) {
      this.emitConfirmData({
        header: this.$t("confirm.delete_label.header"),
        textTitle: label.title,
        text: this.$t("confirm.delete_label.text"),
        actions: {
          confirm: this.$t("button.general.delete"),
          cancel: this.$t("button.general.cancel"),
        },
        callback: (isConfirm) => {
          if (isConfirm) {
            this.showActions = false;
            this.$emit("delete-label", label.id);
          }
        },
      });
    },
    changeShowAction(value, index) {
      if (this.showActions === false) {
        this.showActions = true;
        this.focusedLabel = { ...value };
        this.focusedIndex = index;
      } else {
        this.showActions = false;
        this.showChangeLabel = false;
      }
    },
    changeColor(color) {
      this.focusedLabel.color = color;
    },
    changeToInput() {
      this.showChangeLabel = !this.showChangeLabel;
      if (this.showChangeLabel) {
        setTimeout(() => {
          this.$refs.input[0].focus();
        }, 100);
      }
    },
    updateLabel() {
      if (this.focusedLabel.title.length === 0) {
        this.emitNotification({
          type: "error",
          text: this.$t("notification.label.cannot_empty"),
        });
      } else if (this.focusedLabel.title.length <= 25 && this.isLabelChanged) {
        this.$emit("update-label", this.focusedLabel);
        this.showActions = false;
        this.showChangeLabel = false;
      }
    },
    closeChangeLabel() {
      this.showActions = false;
      this.showChangeLabel = false;
    },
  },
  computed: {
    ...mapGetters(["getLabels", "getTheme"]),
    isLabelChanged() {
      const initial = this.labels.find(
        (item) => item.id === this.focusedLabel.id
      );
      return (
        initial.color !== this.focusedLabel.color ||
        initial.title !== this.focusedLabel.title
      );
    },
    isSelectedChanged() {
      return this.selectedLabelId?.id != this.selected;
    },
  },
  watch: {
    selectedLabelId: {
      handler(value) {
        this.selected = value !== null ? value.id : null;
      },
      immediate: true,
    },
    getLabels: {
      handler(value) {
        this.labels = value;
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style>
@import "index.module.css";
.popover {
  @apply !h-24;
}
</style>
